import { useEffect, useState } from "react";
import { api } from "../../lib/api";

// DevHub structure — the canonical section ordering and which orgs have a corpus —
// served by GET /api/devhub/structure (derived from the corpus, not hardcoded here).
// Fetched once and cached at module scope so every consumer (the Docs menu, the CMS
// pages list, the overview, the public sidebar, and App's view gate) shares one
// request and one answer.

export interface DevhubStructure {
  /** Sections present in the corpus, in canonical order. */
  sections: string[];
  /** Section order DECLARED by a version-root `_order.yaml`, or [] when none exists.
   *  The v1.1 tabbed renderers re-sort sections within a tab by this only when it is
   *  non-empty (an authored order); empty leaves the deployed tab config unchanged. */
  sectionFileOrder: string[];
  /** The same, per org and version — the only form that means anything with more
   *  than one corpus present (ENG-57563). */
  sectionFileOrderByOrg: Record<string, Record<string, string[]>>;
  /** Orgs that have a DevHub corpus. */
  orgs: string[];
  /** Doc versions present (e.g. ["v1.1","v2.0"]), sorted; flat union across orgs. */
  versions: string[];
  /** Doc versions per org. */
  versionsByOrg: Record<string, string[]>;
  /** False until the fetch resolves (so callers don't act on an empty default). */
  loaded: boolean;
}

const EMPTY: DevhubStructure = { sections: [], sectionFileOrder: [], sectionFileOrderByOrg: {}, orgs: [], versions: [], versionsByOrg: {}, loaded: false };

let cache: DevhubStructure | null = null;
let inflight: Promise<DevhubStructure> | null = null;

function load(): Promise<DevhubStructure> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = api
      .getDevhubStructure()
      .then((s) => {
        cache = {
          sections: Array.isArray(s?.sections) ? s.sections : [],
          sectionFileOrder: Array.isArray(s?.sectionFileOrder) ? s.sectionFileOrder : [],
          sectionFileOrderByOrg: s?.sectionFileOrderByOrg && typeof s.sectionFileOrderByOrg === "object" ? s.sectionFileOrderByOrg : {},
          orgs: Array.isArray(s?.orgs) ? s.orgs : [],
          versions: Array.isArray(s?.versions) ? s.versions : [],
          versionsByOrg: s?.versionsByOrg && typeof s.versionsByOrg === "object" ? s.versionsByOrg : {},
          loaded: true,
        };
        return cache;
      })
      .catch(() => {
        inflight = null; // allow a later retry
        return EMPTY;
      });
  }
  return inflight;
}

export function useDevhubStructure(): DevhubStructure {
  const [structure, setStructure] = useState<DevhubStructure>(cache ?? EMPTY);
  useEffect(() => {
    if (cache) { setStructure(cache); return; }
    let cancelled = false;
    void load().then((s) => { if (!cancelled) setStructure(s); });
    return () => { cancelled = true; };
  }, []);
  return structure;
}
